import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Drawer, Select, TextInput, Textarea } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastSuccess } from "../../../shared/toastHelper";
import { errorMessages } from "./staffingFormatters";
import { cardDateLabel, localClockLabel, rangeLabel } from "./timesheetFormatters";

const KIND_OPTIONS = [
  { value: "meal", label: "Meal" },
  { value: "rest", label: "Rest (paid)" },
];

const FIELD_LABELS = { clock_in_at: "In", clock_out_at: "Out", status: "Status" };

const textStyle = (size, color, weight = 400) => ({ fontSize: size, lineHeight: `${size + 6}px`, color: `var(--sl-${color})`, fontWeight: weight });

const breakLabel = (timeCardBreak) => {
  if (!timeCardBreak) return "none";
  if (timeCardBreak.status !== "taken") return `${timeCardBreak.kind} break ${timeCardBreak.status}`;
  return `${timeCardBreak.kind} ${rangeLabel(timeCardBreak.started_at, timeCardBreak.ended_at)}`;
};

const valueLabel = (field, value) => {
  if (field === "break") return breakLabel(value);
  if (field === "status") return value;
  return value ? localClockLabel(value) : "—";
};

export const changeLabel = (change) =>
  `${change.field === "break" ? "Break" : FIELD_LABELS[change.field]} ${valueLabel(change.field, change.before)} → ${valueLabel(change.field, change.after)}`;

const formFromCard = (card) => ({
  clock_in_at: card?.clockInAt || "",
  clock_out_at: card?.clockOutAt || "",
  breaks: (card?.breaks || []).map((timeCardBreak) => ({ ...timeCardBreak, removed: false })),
});

const breakAttributes = (breaks) =>
  breaks
    .filter((timeCardBreak) => timeCardBreak.id || !timeCardBreak.removed)
    .map((timeCardBreak) =>
      timeCardBreak.removed
        ? { id: timeCardBreak.id, _destroy: true }
        : { id: timeCardBreak.id, kind: timeCardBreak.kind, status: timeCardBreak.status, started_at: timeCardBreak.startedAt, ended_at: timeCardBreak.endedAt }
    );

const MEAL_BREAK_ANSWERS = {
  true: "At clock-out they said they got their meal break.",
  false: "At clock-out they said they didn't get their meal break.",
};

const TimeCardDrawer = ({ card, staffChoices, paths, onClose, onSaved }) => {
  const isNew = !card;
  const [form, setForm] = useState(() => formFromCard(card));
  const [person, setPerson] = useState({ staff_member_id: "", venue_ownership_id: staffChoices?.venues?.[0]?.id ?? "", staff_position_id: "" });
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const missedBreak = card?.flags.includes("missed_break");
  const mealBreakAnswer = MEAL_BREAK_ANSWERS[card?.mealBreakAttested];

  useEffect(() => {
    if (isNew) return;
    client
      .get(paths.timeCard.replace(":id", card.id))
      .then(({ data }) => setHistory(data.history))
      .catch(() => setHistory([]));
  }, [isNew, card?.id, paths.timeCard]);

  const people = staffChoices?.people || [];
  const positionOptions = useMemo(
    () => (people.find((choice) => choice.id === person.staff_member_id)?.positions || []).map((position) => ({ value: position.id, label: position.name })),
    [people, person.staff_member_id]
  );

  const setBreak = (index, changes) => setForm((current) => ({ ...current, breaks: current.breaks.map((timeCardBreak, position) => (position === index ? { ...timeCardBreak, ...changes } : timeCardBreak)) }));
  const addBreak = (attributes) => setForm((current) => ({ ...current, breaks: [...current.breaks, { kind: "meal", status: "taken", startedAt: "", endedAt: "", removed: false, ...attributes }] }));
  const markMeal = (status) => addBreak({ kind: "meal", status, startedAt: card.mealBreakDueAt, endedAt: card.mealBreakDueAt });

  const canSave = reason.trim().length > 0 && form.clock_in_at && (!isNew || (form.clock_out_at && person.staff_member_id && person.staff_position_id && person.venue_ownership_id));

  const save = () => {
    if (!canSave) return;
    setSaving(true);
    setErrors([]);
    const timeCard = { clock_in_at: form.clock_in_at, clock_out_at: form.clock_out_at || null, edit_reason: reason, time_card_breaks_attributes: breakAttributes(form.breaks) };
    const request = isNew ? client.post(paths.timeCards, { time_card: { ...timeCard, ...person } }) : client.patch(paths.timeCard.replace(":id", card.id), { time_card: timeCard });
    request
      .then(({ data }) => {
        toastSuccess(isNew ? "Time card added. Approve it when you're ready." : "Time card updated. Approve it when you're ready.");
        onSaved(data.timeCard);
      })
      .catch((error) => setErrors(errorMessages(error).length ? errorMessages(error) : ["Could not save this time card."]))
      .finally(() => setSaving(false));
  };

  const subtitle = isNew
    ? "For a punch the time clock missed."
    : [card.staffMemberName, cardDateLabel(card.clockInAt), card.eventTitle].filter(Boolean).join(" · ");

  return (
    <Drawer
      open
      backdrop
      width={560}
      title={isNew ? "Add time card" : "Edit time card"}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <div className="d-flex w-100 justify-content-end" style={{ gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!canSave || saving} onClick={save}>
            {saving ? "Saving…" : "Save change"}
          </Button>
        </div>
      }
    >
      {errors.length > 0 && (
        <div className="mb-3">
          <Alert color="error" title="Could not save this time card" description={errors.join(" ")} />
        </div>
      )}
      {!isNew && (
        <div className="mb-3" style={textStyle(13, "text-tertiary")}>
          Scheduled {card.shift ? rangeLabel(card.shift.startsAt, card.shift.endsAt) : "none (unscheduled)"} · {card.positionName}
        </div>
      )}
      {isNew && (
        <>
          <div className="mb-3">
            <Select
              id="time-card-person"
              label="Person"
              searchable
              options={people.map((choice) => ({ value: choice.id, label: choice.name }))}
              value={person.staff_member_id}
              onChange={(value) => {
                const positions = people.find((choice) => choice.id === value)?.positions || [];
                setPerson((current) => ({ ...current, staff_member_id: value, staff_position_id: positions.length === 1 ? positions[0].id : "" }));
              }}
            />
          </div>
          <div className="d-flex mb-3" style={{ gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                id="time-card-position"
                label="Position"
                options={positionOptions}
                value={person.staff_position_id}
                onChange={(value) => setPerson((current) => ({ ...current, staff_position_id: value }))}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                id="time-card-venue"
                label="Venue"
                options={(staffChoices?.venues || []).map((venue) => ({ value: venue.id, label: venue.name }))}
                value={person.venue_ownership_id}
                onChange={(value) => setPerson((current) => ({ ...current, venue_ownership_id: value }))}
              />
            </div>
          </div>
        </>
      )}
      <div className="d-flex mb-3" style={{ gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TextInput id="time-card-clock-in" label="Clock in" type="datetime-local" value={form.clock_in_at} onChange={(event) => setForm((current) => ({ ...current, clock_in_at: event.target.value }))} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TextInput id="time-card-clock-out" label="Clock out" type="datetime-local" value={form.clock_out_at} onChange={(event) => setForm((current) => ({ ...current, clock_out_at: event.target.value }))} />
        </div>
      </div>

      <div className="mb-2" style={textStyle(14, "text-secondary", 600)}>
        Breaks
      </div>
      {mealBreakAnswer && <div className="mb-2" style={textStyle(14, "text-secondary")}>{mealBreakAnswer}</div>}
      {form.breaks.map((timeCardBreak, index) =>
        timeCardBreak.removed ? null : (
          <div key={timeCardBreak.id || `new-${index}`} className="d-flex align-items-end mb-2" style={{ gap: 8 }}>
            {timeCardBreak.status === "taken" ? (
              <>
                <div style={{ width: 120 }}>
                  <Select id={`time-card-break-kind-${index}`} label="Kind" options={KIND_OPTIONS} value={timeCardBreak.kind} onChange={(value) => setBreak(index, { kind: value })} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput id={`time-card-break-start-${index}`} label="Start" type="datetime-local" value={timeCardBreak.startedAt || ""} onChange={(event) => setBreak(index, { startedAt: event.target.value })} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput id={`time-card-break-end-${index}`} label="End" type="datetime-local" value={timeCardBreak.endedAt || ""} onChange={(event) => setBreak(index, { endedAt: event.target.value })} />
                </div>
              </>
            ) : (
              <div style={{ flex: 1, ...textStyle(14, "text-secondary") }}>Meal break {timeCardBreak.status}</div>
            )}
            <Button variant="tertiary" size="sm" onClick={() => setBreak(index, { removed: true })}>
              Remove
            </Button>
          </div>
        )
      )}
      <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
        <Button variant="secondary" size="sm" onClick={() => addBreak({})}>
          Add break
        </Button>
        {missedBreak && (
          <>
            <Button variant="secondary" size="sm" onClick={() => markMeal("missed")}>
              Mark meal break missed
            </Button>
            <Button variant="secondary" size="sm" onClick={() => markMeal("waived")}>
              Mark meal break waived
            </Button>
          </>
        )}
      </div>

      <div className="mb-3">
        <Textarea
          id="time-card-reason"
          label="Reason for edit *"
          hint="Saved with the change history. The staff member can see it."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      {history.length > 0 && (
        <>
          <div className="mb-2" style={textStyle(14, "text-secondary", 600)}>
            History
          </div>
          {history
            .slice()
            .reverse()
            .map((entry, index) => (
              <div key={`${entry.at}-${entry.action}-${index}`} className="mb-2" style={{ borderTop: "1px solid var(--sl-border-secondary)", paddingTop: 8 }}>
                <div style={textStyle(13, "text-primary", 600)}>
                  {entry.who} · {new Date(entry.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
                <div style={textStyle(13, "text-tertiary")}>{entry.changes.map(changeLabel).join(" · ")}</div>
                {entry.reason && <div style={textStyle(13, "text-tertiary")}>“{entry.reason}”</div>}
              </div>
            ))}
        </>
      )}
    </Drawer>
  );
};

TimeCardDrawer.propTypes = {
  card: PropTypes.object,
  staffChoices: PropTypes.shape({ venues: PropTypes.array, people: PropTypes.array }),
  paths: PropTypes.shape({ timeCards: PropTypes.string.isRequired, timeCard: PropTypes.string.isRequired }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default TimeCardDrawer;
